import { useEffect, useRef, useState } from "react";
import { PANEL_SELECTOR } from "Hooks/useAllieDrag";

/**
 * Let the user type into Ask Allie while another drawer or modal is open.
 *
 * Any antd overlay with a mask traps focus: `useLockFocus` keeps a global stack of
 * locked elements and, on every focusin, forces focus back into the LAST one. Allie
 * has mask={false} so it never joins that stack — which means once the JD creation
 * drawer (or any masked modal) is open, clicking Allie's input hands focus straight
 * back to the drawer and the keystrokes go there. Allie is clickable (it sits at
 * --z-highest, well above antd's default), it just can't hold focus.
 *
 * Rather than fight the trap, this joins it. antd v6 exposes the lock as a public
 * prop — `focusable={{ trap }}` — so while the user is interacting with Allie we
 * turn Allie's own trap ON. Allie becomes the last locked element, the stack's own
 * "last one wins" rule keeps focus inside it, and typing works. Touch anything
 * outside Allie and the trap goes off again, handing containment back to whatever
 * overlay owns it.
 *
 * @param   {object}  opts
 * @param   {boolean} opts.enabled  true while the Allie panel is open
 * @returns {boolean} pass as `focusable={{ trap }}` on Allie's Drawer
 */
const useAllieFocusPriority = ({ enabled }) => {
  const [trap, setTrap] = useState(false);

  // Where the user was pointing when they left Allie. Releasing the trap is a
  // state update, so the foreign lock can still win the race for that first click
  // and yank focus back into Allie; if that happens we hand it over once, here.
  const pendingTargetRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setTrap(false);
      return undefined;
    }

    const insideAllie = (node) => !!node && !!getPanel()?.contains(node);

    const onPointerDown = (e) => {
      if (insideAllie(e.target)) {
        pendingTargetRef.current = null;
        setTrap(true);
        return;
      }
      // Leaving Allie — drop the trap and remember where they were headed.
      pendingTargetRef.current = e.target;
      setTrap(false);
    };

    // Runs after the trap has been released. If focus landed back in Allie anyway,
    // that was the foreign lock, not the user — send it where they clicked. Bounded
    // to a single correction per click, so there is no ping-pong with the lock.
    const onFocusIn = (e) => {
      const target = pendingTargetRef.current;
      if (!target || !insideAllie(e.target)) return;
      pendingTargetRef.current = null;
      if (typeof target.focus === "function") target.focus({ preventScroll: true });
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn);
      pendingTargetRef.current = null;
    };
  }, [enabled]);

  return trap;
};

const getPanel = () => document.querySelector(PANEL_SELECTOR);

export default useAllieFocusPriority;
